<template>
  <div class="messenger-chat-message-list">
    <div
      v-show="!isLoading && !listState.isAllMessagesLoaded"
      ref="newPageLoaderRef"
      class="new-page-loader"
      :class="{
        'new-page-loader--loading': listState.isNextPageLoading,
      }"
    >
      <widget-spinner />
    </div>

    <div class="message-list">
      <template v-if="isLoading">
        <div class="message-list-item message-list-item--outcoming">
          <widget-skeleton
            width="220px"
            height="60px"
          />
        </div>
        <div class="message-list-item message-list-item--outcoming">
          <widget-skeleton
            width="168px"
            height="40px"
          />
        </div>
        <div class="message-list-item message-list-item--log">
          <widget-skeleton
            width="200px"
            height="10px"
          />
        </div>
      </template>

      <template v-else>
        <template
          v-for="item in listItems"
          :key="item.id"
        >
          <div
            v-if="item.id === firstNewMessageId"
            :id="newMessageDividerId"
            class="new-message-divider"
          >
            {{ $t('chat.new') }}
          </div>
          <div
            class="message-list-item"
            :class="{
              'message-list-item--outcoming': isOutcoming(item),
              'message-list-item--log': isLog(item),
            }"
          >
            <messenger-chat-sent-message
              v-if="isSentMessageListItem(item)"
              :message="item"
              class="message-list-item__text-message"
            />
            <messenger-chat-incoming-message
              v-if="isIncomingMessageListItem(item)"
              :message="item"
              class="message-list-item__text-message"
            />
            <messenger-chat-initial-message
              v-else-if="isInitialMessageListItem(item)"
              :message="item"
              class="message-list-item__text-message"
            />
            <messenger-chat-user-joined-or-left-log-message
              v-else-if="isUserJoinedLogMessageListItem(item) || isUserLeftLogMessageListItem(item)"
              :log-item="item"
            />
            <messenger-chat-rated-log-message
              v-else-if="isChatRatedLogMessageListItem(item)"
              :log-item="item"
            />
            <messenger-chat-comment-left-log-message
              v-else-if="isCommentLeftLogMessageListItem(item)"
              :log-item="item"
              :rate="chatRate"
            />
            <messenger-chat-auto-reply-message
              v-else-if="isAutoReplyMessageListItem(item)"
              :message="item"
              class="message-list-item__text-message"
            />
          </div>
        </template>
      </template>
    </div>

    <div
      v-if="isAgentSearching"
      class="searching-agents"
    >
      <div class="searching-agents__text">{{ $t('chat.searchingOfAvailableAgents') }}</div>
      <widget-spinner />
    </div>
    <div
      v-else
      class="bottom-gap"
    />
  </div>
</template>

<script setup lang="ts">
import {
  isAutoReplyMessageListItem,
  isChatRatedLogMessageListItem,
  isCommentLeftLogMessageListItem,
  isIncomingMessageListItem,
  isInitialMessageListItem,
  isLogMessageListItem,
  isSentMessageListItem,
  isUserJoinedLogMessageListItem,
  isUserLeftLogMessageListItem,
  type MessengerMessageListItem,
} from '@widgets/messenger/types/chat/message'
import WidgetSpinner from '@widgets/shared/components/WidgetSpinner.vue'
import MessengerChatSentMessage from '@widgets/messenger/components/messages/MessengerChatSentMessage.vue'
import MessengerChatInitialMessage from '@widgets/messenger/components/messages/MessengerChatInitialMessage.vue'
import MessengerChatIncomingMessage from '@widgets/messenger/components/messages/MessengerChatIncomingMessage.vue'
import MessengerChatUserJoinedOrLeftLogMessage from '@widgets/messenger/components/messages/MessengerChatUserJoinedOrLeftLogMessage.vue'
import MessengerChatRatedLogMessage from '@widgets/messenger/components/messages/MessengerChatRatedLogMessage.vue'
import MessengerChatCommentLeftLogMessage from '@widgets/messenger/components/messages/MessengerChatCommentLeftLogMessage.vue'
import MessengerChatAutoReplyMessage from '@widgets/messenger/components/messages/MessengerChatAutoReplyMessage.vue'
import WidgetSkeleton from '@widgets/shared/components/WidgetSkeleton.vue'
import type { MessengerMessageListState } from '@widgets/messenger/types/chat/chat'
import { computed, onBeforeUnmount, onMounted, ref } from '@/composition/vue/compositionApi'

const props = defineProps<{
  listItems: MessengerMessageListItem[]
  listState: MessengerMessageListState
  isAgentSearching: boolean
  firstNewMessageId?: number | string
  newMessageDividerId?: string
  isLoading?: boolean
}>()

const emit = defineEmits<{
  (e: 'load-next-page'): void
}>()

const isOutcoming = (item: MessengerMessageListItem) => {
  return !!(isInitialMessageListItem(item) || isSentMessageListItem(item))
}
const isLog = (item: MessengerMessageListItem) => {
  return isLogMessageListItem(item)
}
const chatRate = computed(() => {
  return props.listItems.find(isChatRatedLogMessageListItem)?.rate
})

const newPageLoaderRef = ref<HTMLElement | null>(null)
const newPageLoaderObserver = new IntersectionObserver((entries) => {
  const intersectedLoaderEntry = entries.find((entry) => entry.isIntersecting)
  if (
    intersectedLoaderEntry &&
    !props.listState.isAllMessagesLoaded &&
    !props.listState.isNextPageLoading &&
    !props.isLoading
  ) {
    emit('load-next-page')
  }
})

onMounted(() => {
  newPageLoaderObserver.observe(newPageLoaderRef.value!)
})

onBeforeUnmount(() => {
  newPageLoaderObserver.disconnect()
})
</script>

<style lang="scss" scoped>
@import '@/styles/custom-variables.scss';
@import '@/styles/mixins.scss';

$sidePadding: 16px;

.messenger-chat-message-list {
  padding-top: 16px;
  position: relative;
}

.message-list {
  padding: 0 $sidePadding;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.message-list-item {
  display: flex;

  &--outcoming {
    justify-content: flex-end;
  }

  &--log {
    justify-content: center;
  }

  &__text-message {
    max-width: 300px;
  }
}

.searching-agents {
  padding: 16px 0;
  display: flex;
  flex-direction: column;
  align-items: center;

  &__text {
    font-size: 14px;
    line-height: 20px;
    font-weight: $medium-font;
    color: $neutral;
    margin-bottom: 8px;
  }
}

.bottom-gap {
  height: 24px;
}

.new-message-divider {
  color: $red;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  line-height: 20px;
  font-weight: $semi-bold-font;

  &::before,
  &::after {
    content: '';
    display: block;
    flex-grow: 1;
    flex-shrink: 1;
    height: 1px;
    background: currentColor;
  }
}

.new-page-loader {
  padding-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  visibility: hidden;

  &--loading {
    visibility: visible;
  }
}
</style>
